import { Select as SelectPrimitive } from "@base-ui/react/select";
import {
    CheckIcon,
    ChevronDownIcon
} from "@solomon-labs/design/components/icons";
import * as React from "react";

import { cn } from "../../utils/cn";

import { forcedFieldBorderClasses } from "./force-state";
import { textVariants } from "./text";

const Select = SelectPrimitive.Root;

/** Status state for select triggers that need a visual cue beyond
 *  default/focus/disabled. Mirrors `<Input>` — borders shift to the matching
 *  status-X-500 token (per Figma `Solomon-App-v3.0` node 2157:9111).
 *  `error` additionally sets `aria-invalid` for a11y. */
export type SelectTriggerState = "success" | "warning" | "error";

const SELECT_STATE_BORDER: Record<SelectTriggerState, string> = {
    success: "border-status-success-500",
    warning: "border-status-warning-500",
    error: "border-status-error-500"
};

/** Background surface for the trigger. Defaults to `transparent` (the trigger
 *  reads as part of its container); `sunken` fills it with `bg-surface-sunken`,
 *  etc. */
export type SelectTriggerSurface =
    | "transparent"
    | "sunken"
    | "raised"
    | "ground";

const SELECT_SURFACE_BG: Record<SelectTriggerSurface, string> = {
    transparent: "bg-transparent",
    sunken: "bg-surface-sunken",
    raised: "bg-surface-raised",
    ground: "bg-surface-ground"
};

function SelectGroup({
    className,
    ...props
}: SelectPrimitive.Group.Props): React.ReactElement {
    return (
        <SelectPrimitive.Group
            data-slot="select-group"
            className={cn("scroll-my-1 p-1", className)}
            {...props}
        />
    );
}

function SelectValue({
    className,
    ...props
}: SelectPrimitive.Value.Props): React.ReactElement {
    return (
        <SelectPrimitive.Value
            data-slot="select-value"
            className={cn("flex flex-1 text-left", className)}
            {...props}
        />
    );
}

function SelectTrigger({
    className,
    size = "default",
    state,
    surface = "transparent",
    forceHover = false,
    forceFocus = false,
    children,
    ...props
}: SelectPrimitive.Trigger.Props & {
    size?: "sm" | "default" | "lg";
    /** Status state — shifts the border to the matching status color.
     *  `error` also sets `aria-invalid` so screen readers + native validation
     *  styling pick it up. */
    state?: SelectTriggerState;
    /** Background surface token. Defaults to `transparent`. */
    surface?: SelectTriggerSurface;
    /** Demo affordance — force the hover VISUAL (darker border) on with no
     *  pointer present, for scripted sims. Mirrors `<Input forceHover>`.
     *  A `state` prop still wins. Default `false`. */
    forceHover?: boolean;
    /** Demo affordance — force the focus VISUAL (focus border) on. Wins over
     *  `forceHover`. Default `false`. */
    forceFocus?: boolean;
}): React.ReactElement {
    return (
        <SelectPrimitive.Trigger
            data-slot="select-trigger"
            data-size={size}
            {...(state === "error" ? { "aria-invalid": true } : {})}
            className={cn(
                textVariants({ variant: "body" }),
                "flex w-fit items-center justify-between gap-1.5 rounded-none border py-2 pr-2 pl-2.5",
                SELECT_SURFACE_BG[surface],
                "text-foreground-base whitespace-nowrap",
                "transition-colors select-none",
                // Border-state cascade — borders communicate state, not rings
                // (per Figma `Solomon-App-v3.0` node 2157:9111). Default →
                // hover → focus all live on this primitive; the status states
                // (success/warning/error) come from the `state` prop and win
                // over the interaction states because they trail the cascade.
                "border-line-base hover:border-charcoal-700",
                "focus-visible:outline-none focus-visible:border-charcoal-500",
                "disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-line-neutral disabled:text-foreground-imprint",
                // Forced demo hover/focus (sim): literal border tokens so the
                // trigger border darkens without a pointer. Precedes `state`
                // so a status border still wins.
                forcedFieldBorderClasses(forceHover, forceFocus),
                "data-placeholder:text-foreground-neutral",
                "data-[size=default]:h-8 data-[size=sm]:h-7 data-[size=lg]:h-12",
                "*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5",
                "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                // Status states win over interaction states via prop-driven
                // className order (cn's tailwind-merge collapses the cascade).
                state && SELECT_STATE_BORDER[state],
                className
            )}
            {...props}
        >
            {children}
            <SelectPrimitive.Icon className="pointer-events-none flex shrink-0 items-center text-foreground-neutral">
                <ChevronDownIcon className="size-4" />
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    );
}

function SelectContent({
    className,
    children,
    side = "bottom",
    sideOffset = 4,
    align = "center",
    alignOffset = 0,
    alignItemWithTrigger = true,
    container,
    ...props
}: SelectPrimitive.Popup.Props &
    Pick<
        SelectPrimitive.Positioner.Props,
        "align" | "alignOffset" | "side" | "sideOffset" | "alignItemWithTrigger"
    > & {
        /** Sim/demo-only — portal the dropdown INTO this element instead of
         *  `<body>` (Base UI's default). Scripted sims pass their cursor
         *  container so the portaled `SelectItem`s land inside the element the
         *  simulated pointer is anchored to and can be reached by `moveTo`.
         *  Omit in production → Base UI portals to `<body>` as before. */
        container?: SelectPrimitive.Portal.Props["container"];
    }): React.ReactElement {
    return (
        <SelectPrimitive.Portal container={container}>
            <SelectPrimitive.Positioner
                side={side}
                sideOffset={sideOffset}
                align={align}
                alignOffset={alignOffset}
                alignItemWithTrigger={alignItemWithTrigger}
                className="isolate z-50"
            >
                <SelectPrimitive.Popup
                    data-slot="select-content"
                    data-align-trigger={alignItemWithTrigger}
                    className={cn(
                        textVariants({ variant: "body" }),
                        "relative isolate z-50 max-h-[var(--available-height)] w-[var(--anchor-width)] min-w-36 origin-[var(--transform-origin)] overflow-x-hidden overflow-y-auto rounded-none border border-line-base bg-paper-200 outline-none",
                        "text-foreground-base",
                        "data-[align-trigger=true]:animate-none",
                        "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
                        "data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2",
                        "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
                        "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
                        "duration-100",
                        className
                    )}
                    {...props}
                >
                    <SelectScrollUpButton />
                    <SelectPrimitive.List>{children}</SelectPrimitive.List>
                    <SelectScrollDownButton />
                </SelectPrimitive.Popup>
            </SelectPrimitive.Positioner>
        </SelectPrimitive.Portal>
    );
}

function SelectLabel({
    className,
    ...props
}: SelectPrimitive.GroupLabel.Props): React.ReactElement {
    return (
        <SelectPrimitive.GroupLabel
            data-slot="select-label"
            className={cn(
                textVariants({ variant: "fine-print" }),
                "px-1.5 py-1 text-foreground-neutral",
                className
            )}
            {...props}
        />
    );
}

function SelectItem({
    className,
    children,
    forceHighlighted = false,
    suppressAutoHighlight = false,
    ...props
}: SelectPrimitive.Item.Props & {
    /** Demo/sim affordance — paint Base UI's HIGHLIGHTED-option visual
     *  (`data-highlighted` styling: `bg-surface-sunken` + `text-foreground-base`)
     *  with no real pointer/keyboard focus, so a scripted cursor hovering the
     *  option lights it up. Mirrors `<SelectTrigger forceHover>`. Default
     *  `false` → production items unaffected. */
    forceHighlighted?: boolean;
    /** Demo/sim affordance — drop the intrinsic `data-highlighted` / `focus`
     *  auto-highlight styling so ONLY {@link forceHighlighted} paints. Base UI
     *  auto-highlights the currently-selected item when a Select opens; in a
     *  scripted cursor walk that would light a second option (the pre-selected
     *  one) alongside the one under the cursor. Setting this in sim mode keeps
     *  exactly one option highlighted — the one the scripted pointer is over.
     *  Default `false` → production items keep their normal highlight. */
    suppressAutoHighlight?: boolean;
}): React.ReactElement {
    return (
        <SelectPrimitive.Item
            data-slot="select-item"
            className={cn(
                textVariants({ variant: "body" }),
                "relative flex w-full cursor-default items-center gap-1.5 rounded-none py-1 pr-8 pl-1.5 select-none outline-none",
                // Intrinsic auto-highlight (real pointer/keyboard focus + Base
                // UI's selected-item-on-open). Suppressed in sim so only the
                // scripted forced highlight paints — one option at a time.
                !suppressAutoHighlight &&
                    "focus:bg-surface-sunken focus:text-foreground-base data-highlighted:bg-surface-sunken data-highlighted:text-foreground-base",
                "data-disabled:pointer-events-none data-disabled:opacity-50",
                "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                "*:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
                // Forced demo highlight (sim): mirror the highlighted-option
                // visual without a real pointer. Trails the cascade so it wins.
                forceHighlighted && "bg-surface-sunken text-foreground-base",
                className
            )}
            {...props}
        >
            <SelectPrimitive.ItemText className="flex flex-1 shrink-0 gap-2 whitespace-nowrap">
                {children}
            </SelectPrimitive.ItemText>
            <SelectPrimitive.ItemIndicator
                render={
                    <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />
                }
            >
                <CheckIcon className="size-4" />
            </SelectPrimitive.ItemIndicator>
        </SelectPrimitive.Item>
    );
}

function SelectSeparator({
    className,
    ...props
}: SelectPrimitive.Separator.Props): React.ReactElement {
    return (
        <SelectPrimitive.Separator
            data-slot="select-separator"
            className={cn(
                "pointer-events-none -mx-1 my-1 h-px bg-line-base",
                className
            )}
            {...props}
        />
    );
}

function SelectScrollUpButton({
    className,
    ...props
}: React.ComponentProps<
    typeof SelectPrimitive.ScrollUpArrow
>): React.ReactElement {
    return (
        <SelectPrimitive.ScrollUpArrow
            data-slot="select-scroll-up-button"
            className={cn(
                "top-0 z-10 flex w-full cursor-default items-center justify-center bg-paper-200 py-1 [&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        >
            <svg
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <path
                    d="M4 10l4-4 4 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </SelectPrimitive.ScrollUpArrow>
    );
}

function SelectScrollDownButton({
    className,
    ...props
}: React.ComponentProps<
    typeof SelectPrimitive.ScrollDownArrow
>): React.ReactElement {
    return (
        <SelectPrimitive.ScrollDownArrow
            data-slot="select-scroll-down-button"
            className={cn(
                "bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-paper-200 py-1 [&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        >
            <svg
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <path
                    d="M4 6l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </SelectPrimitive.ScrollDownArrow>
    );
}

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue
};
